import ReactSelect, { createFilter } from "react-select";
import classNames from "classnames";
import Label from "../../../../../../app/javascript/components/shared/form/Label";
import type { CatalogFieldProps, CatalogGroup, TagOption } from "./types";

// Search the label only: the default also matches the tag id, so number searches hit random entries
const filterOption = createFilter<TagOption>({
  stringify: (option) => option.label,
});

// Ebene 2 is the group heading, Ebene 1 shows once above the first group of its area
type GroupType = {
  label: string;
  areaTitle?: string;
  firstOfArea: boolean;
  options: TagOption[];
};

function toGroup(group: CatalogGroup): GroupType {
  return {
    label: group.title,
    areaTitle: group.area_title,
    firstOfArea: Boolean(group.first_of_area),
    options: group.entries.map((entry) => ({
      label: entry.label,
      value: entry.tag_id,
    })),
  };
}

export default function SpvCatalogSelect({
  catalog,
  selection,
  onChange,
  required,
  invalid,
}: CatalogFieldProps) {
  const options = catalog.groups.map(toGroup);

  const byId = new Map(
    options
      .flatMap((group) => group.options)
      .map((option) => [option.value, option]),
  );
  // a selection outside the options is a dropped entry of an older post, shown under its own name
  const value = selection.map((tag) => byId.get(tag.value) ?? tag);

  return (
    <div className="control-group">
      <Label label={catalog.title} required={required} />
      <div className="controls remove-input-txt-border">
        <ReactSelect<TagOption, boolean, GroupType>
          aria-label={catalog.title}
          isSearchable
          filterOption={filterOption}
          isClearable
          isMulti={catalog.multiple}
          closeMenuOnSelect={!catalog.multiple}
          placeholder={`${catalog.title} auswählen …`}
          options={options}
          value={catalog.multiple ? value : (value[0] ?? null)}
          onChange={(selected) => {
            const picked = Array.isArray(selected)
              ? selected
              : selected
                ? [selected as TagOption]
                : [];
            onChange(
              picked.map(({ label, value: id }) => ({ label, value: id })),
            );
          }}
          formatGroupLabel={(group) => (
            <>
              {group.firstOfArea && group.areaTitle && (
                <div className="normal-case text-sm font-bold text-base-content border-t border-neutral pt-2 mt-1 mb-1 tracking-normal">
                  {group.areaTitle}
                </div>
              )}
              <div>{group.label}</div>
            </>
          )}
          noOptionsMessage={() => I18n.t("js.plugins.select2.no_match")}
          className={classNames("form-select-container", {
            "field-error field-error-live": invalid,
          })}
          classNamePrefix="form-select"
          unstyled
        />
      </div>
    </div>
  );
}
